import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { IoCloseSharp } from 'react-icons/io5';
import CustomCalendar from '../common/CustomCalendar';
import { formatDateKey, parseDateKey } from '../../utils/helpers';
import { parseTimeInput, formatTimeSpent } from '../../utils/timeTracking';

import { LoaderButton } from '../common/LoadingButton';
import RequiredMark from 'components/common/RequiredMark';

export interface LogTimeData {
  dateStarted: string;
  timeSpent: number; // in minutes
  timeSpentStr: string;
  description: string;
  timeEntryUuid?: string;
}

interface LogTimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: LogTimeData) => void;
  initialData?: LogTimeData | null;
  existingUserLogs?: any[];
}

const LogTimeModal: React.FC<LogTimeModalProps> = ({ isOpen, onClose, onSave, initialData, existingUserLogs = [] }) => {
  const [dateStarted, setDateStarted] = useState<string>('');
  const [timeSpentStr, setTimeSpentStr] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setDateStarted(initialData.dateStarted);
        setTimeSpentStr(initialData.timeSpentStr);
        setDescription(initialData.description || '');
      } else {
        setDateStarted(formatDateKey(new Date()));
        setTimeSpentStr('');
        setDescription('');
      }
      setError(null);
      setIsSaving(false);
    }
  }, [isOpen, initialData]);

  const handleSave = async () => {
    if (!dateStarted) {
      setError('Date Started is required');
      return;
    }

    const selectedDate = parseDateKey(dateStarted);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      setError('Future dates are not allowed');
      return;
    }

    if (!timeSpentStr.trim()) {
      setError('Time Spent is required');
      return;
    }

    const parsedMinutes = parseTimeInput(timeSpentStr);

    if (parsedMinutes === null) {
      setError('Invalid time format. Please enter time in valid format (e.g., 2h, 30m, 2h 30m, 2h30m).');
      return;
    }

    if (parsedMinutes <= 0) {
      setError('Time spent must be greater than 0.');
      return;
    }

    if (parsedMinutes > 24 * 60) {
      setError('Max limit per logged entry cannot exceed 24h');
      return;
    }

    // Filter logs for the selected date to check the 24h daily limit
    const dayLoggedMinutes = existingUserLogs
      .filter(log => log.dateStarted === dateStarted)
      .filter(log => log.timeEntryUuid !== initialData?.timeEntryUuid) // Exclude current entry if editing
      .reduce((sum, log) => sum + (log.minutesSpent || 0), 0);

    if (dayLoggedMinutes + parsedMinutes > 24 * 60) {
      setError(`Unable to log time. Total logged time for ${selectedDate.toLocaleDateString()} cannot exceed 24 hours.`);
      return;
    }

    setIsSaving(true);
    // Simulate a small delay for the loader effect
    await new Promise(resolve => setTimeout(resolve, 600));

    const normalizedTimeStr = formatTimeSpent(parsedMinutes);

    onSave({
      dateStarted,
      timeSpent: parsedMinutes,
      timeSpentStr: normalizedTimeStr,
      description,
    });
    setIsSaving(false);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" onClose={onClose} className="relative z-[60]">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="mx-auto w-full max-w-md rounded-xl bg-primary border border-primary-subtle text-white shadow-2xl overflow-hidden flex flex-col">
              <div className="flex justify-between items-center px-6 py-4 border-b border-primary-subtle">
                <h2 className="text-xl font-semibold">Log Time</h2>
                <button onClick={onClose} className="text-neutral-medium hover:text-white transition-colors">
                  <IoCloseSharp size={24} />
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-white mb-2">
                      Date Started <RequiredMark />
                    </label>
                    <CustomCalendar
                      value={dateStarted ? parseDateKey(dateStarted) : ''}
                      maxDate={new Date()}
                      onChange={(date: Date) => {
                        setDateStarted(formatDateKey(date));
                        setError(null);
                      }}
                      className="w-full bg-primary-variant border border-primary-subtle rounded-lg h-[42px]"
                    />
                  </div>

                  <div className="flex-1">
                    <label className="block text-sm font-medium text-white mb-2">
                      Time Spent <RequiredMark />
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 2h 30m, 120m"
                      value={timeSpentStr}
                      onChange={(e) => {
                        setTimeSpentStr(e.target.value);
                        setError(null);
                      }}
                      className="w-full bg-primary-variant border border-primary-subtle rounded-lg h-[42px] px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                    />
                    <p className="text-[9px] text-neutral-medium mt-1">
                      Use format: 2h 30m (e.g., 2h, 45m, 120m, 2h30m)
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Work Description <span className="text-neutral-medium">(optional)</span>
                  </label>
                  <textarea
                    placeholder="Briefly describe your work (e.g., 'Migration script logic update...')"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    className="w-full bg-primary-variant border border-primary-subtle rounded-lg p-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm resize-none"
                  />
                </div>

                {error && <p className="text-red-500 text-sm">{error}</p>}
              </div>

              <div className="px-6 py-4 border-t border-primary-subtle flex items-center justify-end gap-3 bg-primary-variant/30">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-neutral-medium hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <LoaderButton
                  onClick={handleSave}
                  isLoading={isSaving}
                  text="Save"
                  className="!mt-0 !w-fit px-8 py-2"
                />
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default LogTimeModal;
